import { Warning } from "@phosphor-icons/react"
import type { ColDef, IRowNode } from "ag-grid-community"
import { addDays } from "date-fns"
import { max, min } from "lodash"

import {
  OptimalOrderDateFieldV2,
  optimalOrderDateTooltipText,
} from "~/components/planning/ProductTable/OptimalOrderDateField"
import { AdjustmentCountBadge } from "~/components/reusable/AdjustmentCountBadge"
import type { ICollectionDetails } from "~/hooks/useCollections"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { createCustomColumn, createDateColumn, createNumberColumn } from "~/utils/columnTemplates"
import { formatDate, getStartOfCurrentWeekUTC } from "~/utils/datetime"
import { isLifecycleInactive } from "~/utils/lifecycleUtils"
import { extractVariantSummaries, ProductTableColumn } from "~/utils/productTableUtils"
import { isFashion } from "~/utils/productTypeUtils"
import { filterDefined } from "~/utils/tsUtils"

import { aggregators } from "../utils/aggregators"
import { getStatusFlag } from "../utils/statusFlagUtils"
import type { ColumnSpec } from "./types"

export type DelayedPoWarning = {
  poName: string
  poExpectedArrivalDate: Date
  allPos: {
    poName: string
    warehouseId: string
    warehouseName: string
    poExpectedArrivalDate: Date
  }[]
}

export interface ActionsColumnCtx {
  collections: ICollectionDetails[] | undefined
  phasingOutSellDownForecast: boolean | null | undefined
  /** Variants whose purchase suggestion is snoozed, by variant id. */
  snoozedUntilMap: Map<string, Date> | undefined
  /** Variants with a delayed purchase order, by variant id. */
  delayedPoWarningMap: Map<string, DelayedPoWarning> | undefined
}

type ActionsSpec = ColumnSpec<IVariantSummary, ActionsColumnCtx>

function firstVariantId(node: IRowNode<IVariantSummary>): string | undefined {
  return node.data?.variantId ?? node.allLeafChildren?.[0]?.data?.variantId
}

function snoozedUntilOf(ctx: ActionsColumnCtx, node: IRowNode<IVariantSummary>) {
  const variantId = firstVariantId(node)
  return variantId === undefined ? undefined : ctx.snoozedUntilMap?.get(variantId)
}

/** What the Flags cell and its tooltip both read off a set of variants. */
function flagSignals(entries: IVariantSummary[]) {
  const noosEntries = entries.filter((v) => v?.settings?.productType === "noos")
  const fashionEntries = entries.filter((v) => isFashion(v))
  return {
    lifecycleStatus: aggregators.mode(entries.map((entry) => entry?.settings?.lifecycleStatus)),
    forecastQuantity: aggregators.sum(entries.map((entry) => entry.forecastQuantity)),
    outOfStockDates: noosEntries.flatMap((v) => v.outOfStockInformation),
    outOfStockDatesFashion: fashionEntries.flatMap((v) => v.outOfStockInformation),
  }
}

export const FLAGS_COLUMN: ActionsSpec = {
  colId: ProductTableColumn.Flags,
  render: (params, ctx) => {
    const entries: IVariantSummary[] = extractVariantSummaries(params)
    const { lifecycleStatus, forecastQuantity, outOfStockDates, outOfStockDatesFashion } =
      flagSignals(entries)

    const outOfStockCount = outOfStockDates.filter((s) => s?.status === "stockout").length
    const outOfStockCountFashion = outOfStockDatesFashion.filter(
      (s) => s?.status === "stockout",
    ).length
    const shouldReorderNowCount = outOfStockDates.filter((s) => s?.status === "reorder").length

    const variantsWithWarnings = entries.filter(
      (sku) =>
        !sku.soldSince &&
        sku.activatedFrom &&
        sku.forecastQuantity > 0 &&
        sku.incomingOrders.length === 0 &&
        sku.stockLevel == 0 &&
        (sku.settings.activatedFrom ??
          max(
            filterDefined([
              ...sku.collections.map(
                (col) =>
                  ctx.collections
                    ?.filter((c) => !c.endDate || c.endDate > getStartOfCurrentWeekUTC())
                    .find((c) => c.id === Number(col.id))?.startDate,
              ),
            ]),
          ) ??
          getStartOfCurrentWeekUTC()) < addDays(getStartOfCurrentWeekUTC(), -14),
    )

    return (
      <div className="flex gap-1 items-center">
        {getStatusFlag(lifecycleStatus, forecastQuantity, false)}
        <div className="flex items-center space-x-1">
          {outOfStockCount > 0 && (
            <div className="bg-red-100 text-red-500 rounded-full h-[22px] min-w-[22px] px-[6px] pt-[1px]">
              <div className="h-full flex justify-center items-center text-[11px] font-medium leading-none">
                {outOfStockCount}
              </div>
            </div>
          )}
          {outOfStockCountFashion > 0 && (
            <div className="bg-neutral-100 text-neutral-500 rounded-full h-[22px] min-w-[22px] px-[6px] pt-[1px]">
              <div className="h-full flex justify-center items-center text-[11px] font-medium leading-none">
                {outOfStockCountFashion}
              </div>
            </div>
          )}
          {shouldReorderNowCount > 0 && (
            <div className="bg-orange-100 text-orange-500 rounded-full h-[22px] min-w-[22px] px-[6px] pt-[1px]">
              <div className="h-full flex justify-center items-center text-[11px] font-medium leading-none">
                {shouldReorderNowCount}
              </div>
            </div>
          )}
        </div>
        {variantsWithWarnings.length > 0 && <Warning />}
      </div>
    )
  },
  colDef: (ctx) =>
    createCustomColumn<IVariantSummary>({
      colId: ProductTableColumn.Flags,
      field: "adjustmentCount",
      aggFunc: "max",
      tooltipComponentParams: { tooltipType: "Flags" },
      tooltip: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)
        const { lifecycleStatus, forecastQuantity, outOfStockDates, outOfStockDatesFashion } =
          flagSignals(entries)
        let flagMessage: string | undefined = undefined
        if (lifecycleStatus === "phasingOut") {
          flagMessage = ctx.phasingOutSellDownForecast
            ? "Is being phased out. Forecast shown as sell-down to stockout, no suggested purchases."
            : "Is being phased out. No forecasts or suggested purchases are generated."
        } else if (lifecycleStatus === "phasedOut") {
          flagMessage = "Is phased out. No forecasts or suggested purchases are generated."
        } else if (forecastQuantity === null) {
          flagMessage = "Insufficient data to provide a forecast."
        }

        return {
          flagMessage,
          variants: entries,
          outOfStockDates,
          outOfStockDatesFashion,
        }
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    }),
}

export const ADJUSTMENTS_COLUMN: ActionsSpec = {
  colId: ProductTableColumn.Adjustments,
  render: (params) => {
    const entries: IVariantSummary[] = extractVariantSummaries(params)

    const count = entries.reduce((acc, entry) => entry.adjustmentCount + acc, 0)

    return <div className="flex gap-1">{count > 0 && <AdjustmentCountBadge count={count} />}</div>
  },
  colDef: () =>
    createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.Adjustments,
      valueGetter: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)

        return entries.reduce((acc, entry) => acc + entry.adjustmentCount, 0)
      },
      tooltipComponentParams: { tooltipType: "Adjustment" },
      tooltip: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)

        if (!entries.some((entry) => entry.adjustmentCount > 0)) return
        return entries
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    }),
}

export const OPTIMAL_ORDER_DATE_COLUMN: ActionsSpec = {
  colId: ProductTableColumn.OptimalOrderDate,
  calculate: (row) => min(row.outOfStockInformation?.map((o) => o?.safetyStockOutDate)),
  render: (params, ctx) => {
    if (params.node.footer) return ""
    const entries = extractVariantSummaries(params)
    if (isLifecycleInactive(entries)) return "-"
    const snoozedUntil = params.data ? ctx.snoozedUntilMap?.get(params.data.variantId) : undefined
    if (snoozedUntil) {
      return (
        <span className="text-fg-muted paragraph-sm">Snoozed until {formatDate(snoozedUntil)}</span>
      )
    }
    return <OptimalOrderDateFieldV2 entries={entries} />
  },
  colDef: (ctx) =>
    createDateColumn<IVariantSummary>({
      colId: ProductTableColumn.OptimalOrderDate,
      minWidth: 170,
      cellClass: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)
        if (isLifecycleInactive(entries)) return "text-fg-muted"
        if (params.data && ctx.snoozedUntilMap?.has(params.data.variantId)) return "text-fg-muted"
      },
      tooltip: (params) => {
        const entries = extractVariantSummaries(params)
        if (isLifecycleInactive(entries))
          return { unavailableMessage: "No suggested purchases. No reorder date calculated" }
        const snoozedUntil = params.data
          ? ctx.snoozedUntilMap?.get(params.data.variantId)
          : undefined
        if (snoozedUntil) return `Snoozed until ${formatDate(snoozedUntil)}. Suggestion suppressed.`
        return optimalOrderDateTooltipText(entries)
      },
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const aSnoozedUntil = snoozedUntilOf(ctx, nodeA)
        const bSnoozedUntil = snoozedUntilOf(ctx, nodeB)
        if (aSnoozedUntil && !bSnoozedUntil) return -1
        if (!aSnoozedUntil && bSnoozedUntil) return 1
        if (aSnoozedUntil && bSnoozedUntil) {
          return aSnoozedUntil < bSnoozedUntil ? -1 : aSnoozedUntil > bSnoozedUntil ? 1 : 0
        }
        if (valueA == null && valueB == null) return 0
        if (valueA == null) return 1
        if (valueB == null) return -1
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      },
      cellStyle: { display: "flex", justifyContent: "left" },
    }),
}

export const EXISTING_PO_COLUMN: ActionsSpec = {
  colId: ProductTableColumn.ExistingPO,
  render: (params, ctx) => {
    if (!params.data) return null
    const warning = ctx.delayedPoWarningMap?.get(params.data.variantId)
    if (!warning) return null
    return <Warning className="text-red-500 shrink-0" size={15} weight="fill" />
  },
  colDef: (ctx): ColDef<IVariantSummary> => ({
    colId: ProductTableColumn.ExistingPO,
    headerName: "Existing PO",
    width: 100,
    minWidth: 90,
    sortable: true,
    filter: false,
    valueGetter: (params) => {
      const variantId = params.node ? firstVariantId(params.node) : params.data?.variantId
      return variantId && ctx.delayedPoWarningMap?.has(variantId) ? 1 : 0
    },
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    tooltip: (params) => {
      if (!params.data) return undefined
      const warning = ctx.delayedPoWarningMap?.get(params.data.variantId)
      if (!warning) return undefined
      return {
        tooltipType: "DelayedPoWarning",
        allPos: warning.allPos,
      }
    },
  }),
}

/** In composition order; each is placed at its own position in the table's array. */
export const ACTIONS_COLUMN_SPECS: readonly ActionsSpec[] = [
  FLAGS_COLUMN,
  ADJUSTMENTS_COLUMN,
  OPTIMAL_ORDER_DATE_COLUMN,
  EXISTING_PO_COLUMN,
]
